import {createFileRoute} from '@tanstack/react-router';

import {parseIsoDay, parseNonEmpty, parsePage} from '@/lib/search-params';
import {ReimbursementLedgerPage} from '@/modules/reimbursements/pages/ReimbursementLedgerPage';

/**
 * URL state for the reimbursement transaction ledger. All fields optional so
 * links from elsewhere can land here without params; the page applies concrete
 * defaults (`page ?? 1`, empty filters). `q` is a client-side page search —
 * the API has no text search — kept in the URL for shareable views.
 *
 * Mirrors `routes/_authenticated/advances/ledger.tsx`. Access is enforced by
 * the `/reimbursements/ledger` rule in `lib/permissions.ts`, not here.
 */
export interface ReimbursementLedgerSearch {
  /** A SITE id — the filter is labelled by the site's SQL Account code (DERP-233). */
  site?: string;
  /** Inclusive PAYOUT-date bounds, ISO `yyyy-MM-dd`. Server-side filters. */
  from?: string;
  to?: string;
  q?: string;
  page?: number;
}

/**
 * Exported so the URL contract is unit-testable: `renderWithProviders`
 * registers a test route at `path: initialPath`, so a query string there never
 * matches and search params can't be driven through a rendered page.
 */
export const validateLedgerSearch = (
  search: Record<string, unknown>,
): ReimbursementLedgerSearch => ({
  site: parseNonEmpty(search.site),
  from: parseIsoDay(search.from),
  to: parseIsoDay(search.to),
  q: parseNonEmpty(search.q),
  page: parsePage(search.page),
});

export const Route = createFileRoute('/_authenticated/reimbursements/ledger')({
  validateSearch: validateLedgerSearch,
  component: ReimbursementLedgerPage,
});
